import React, { useState, useEffect, useRef } from "react";

// Window 타입 확장
declare global {
    interface Window {
        kakao: any;
    }
}

// 타입 정의
interface MarkerData {
    id: string;
    name: string;
    position: { lat: number; lng: number };
    type: "good" | "normal" | "warning" | "danger";
    sensorCount: number;
    data: {
        nh3: number;
        h2s: number;
        ch3sh: number;
        tvoc: number;
        temperature: number;
        humidity: number;
        windDirection: string;
        windSpeed: number;
    };
    sensors: {
        [key: string]: {
            nh3: number;
            h2s: number;
            ch3sh: number;
            tvoc: number;
        };
    };
}

// 더미 데이터
const mockData: MarkerData[] = [
    {
        id: "1",
        name: "옥곡리목장",
        position: { lat: 35.0156, lng: 126.7108 },
        type: "good",
        sensorCount: 5,
        data: {
            nh3: 8.23,
            h2s: 1.07,
            ch3sh: 1.05,
            tvoc: 157.67,
            temperature: 17.4,
            humidity: 21,
            windDirection: "북동",
            windSpeed: 0.7,
        },
        sensors: {
            SENSOR1: { nh3: 8.5, h2s: 1.2, ch3sh: 1.1, tvoc: 160 },
            SENSOR2: { nh3: 8.1, h2s: 1.0, ch3sh: 1.0, tvoc: 155 },
            SENSOR3: { nh3: 8.3, h2s: 1.1, ch3sh: 1.05, tvoc: 158 },
            SENSOR4: { nh3: 8.2, h2s: 1.05, ch3sh: 1.03, tvoc: 156 },
            SENSOR5: { nh3: 8.0, h2s: 0.95, ch3sh: 1.0, tvoc: 157 },
        },
    },
    {
        id: "2",
        name: "금천면농장",
        position: { lat: 35.0356, lng: 126.7308 },
        type: "normal",
        sensorCount: 3,
        data: {
            nh3: 12.5,
            h2s: 2.3,
            ch3sh: 2.1,
            tvoc: 245.3,
            temperature: 16.2,
            humidity: 23,
            windDirection: "서",
            windSpeed: 1.2,
        },
        sensors: {
            SENSOR1: { nh3: 12.8, h2s: 2.5, ch3sh: 2.2, tvoc: 250 },
            SENSOR2: { nh3: 12.3, h2s: 2.2, ch3sh: 2.0, tvoc: 242 },
            SENSOR3: { nh3: 12.5, h2s: 2.3, ch3sh: 2.1, tvoc: 245 },
        },
    },
    {
        id: "3",
        name: "다시면양돈장",
        position: { lat: 34.9956, lng: 126.7508 },
        type: "warning",
        sensorCount: 4,
        data: {
            nh3: 25.3,
            h2s: 4.5,
            ch3sh: 4.2,
            tvoc: 456.7,
            temperature: 18.5,
            humidity: 19,
            windDirection: "남서",
            windSpeed: 2.1,
        },
        sensors: {
            SENSOR1: { nh3: 26.0, h2s: 4.8, ch3sh: 4.5, tvoc: 470 },
            SENSOR2: { nh3: 25.0, h2s: 4.3, ch3sh: 4.0, tvoc: 450 },
            SENSOR3: { nh3: 25.3, h2s: 4.5, ch3sh: 4.2, tvoc: 456 },
            SENSOR4: { nh3: 25.2, h2s: 4.5, ch3sh: 4.2, tvoc: 455 },
        },
    },
    {
        id: "4",
        name: "봉황면축사",
        position: { lat: 35.0056, lng: 126.6908 },
        type: "danger",
        sensorCount: 2,
        data: {
            nh3: 45.6,
            h2s: 8.9,
            ch3sh: 8.5,
            tvoc: 789.3,
            temperature: 19.2,
            humidity: 25,
            windDirection: "북",
            windSpeed: 1.5,
        },
        sensors: {
            SENSOR1: { nh3: 46.2, h2s: 9.2, ch3sh: 8.8, tvoc: 800 },
            SENSOR2: { nh3: 45.0, h2s: 8.6, ch3sh: 8.2, tvoc: 780 },
        },
    },
];

const AIView = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const overlaysRef = useRef<any[]>([]);
    const infoWindowRef = useRef<any>(null);

    const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);
    const [selectedSensor, setSelectedSensor] = useState<string>("종합");
    const [currentTime, setCurrentTime] = useState(new Date());
    const [mapLoaded, setMapLoaded] = useState(false);

    // 시간 업데이트
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // 풍향별 각도 변환
    const getWindAngle = (direction: string): number => {
        const angles: { [key: string]: number } = {
            '북': 180,
            '북동': 225,
            '동': 270,
            '남동': 315,
            '남': 0,
            '남서': 45,
            '서': 90,
            '북서': 135
        };
        return angles[direction] || 0;
    };

    // 바람 방향에 따른 오프셋 계산
    const getWindOffset = (direction: string): { x: number; y: number } => {
        // 각 방향별로 확산 이미지의 중심이 마커에 오도록 오프셋 조정
        const offsets: { [key: string]: { x: number; y: number } } = {
            '북': { x: 0, y: -120 },      // 북풍 → 남쪽으로 확산
            '북동': { x: 110, y: -110 },   // 북동풍 → 남서쪽으로 확산
            '동': { x: -110, y: 0 },     // 동풍 → 서쪽으로 확산
            '남동': { x: -80, y: -80 },  // 남동풍 → 북서쪽으로 확산
            '남': { x: 0, y: -110 },     // 남풍 → 북쪽으로 확산
            '남서': { x: -90, y: 80 },   // 남서풍 → 북동쪽으로 확산
            '서': { x: -120, y: 0 },      // 서풍 → 동쪽으로 확산
            '북서': { x: 80, y: 80 }     // 북서풍 → 남동쪽으로 확산
        };
        return offsets[direction] || { x: 0, y: 0 };
    };

    // 카카오맵 스크립트 로드
    useEffect(() => {
        const initializeMap = () => {
            const container = mapRef.current;
            if (!container || !window.kakao || !window.kakao.maps) return;

            try {
                const options = {
                    center: new window.kakao.maps.LatLng(35.0156, 126.7108),
                    level: 8,
                    draggable: true,
                    scrollwheel: true,
                    disableDoubleClick: false,
                    keyboardShortcuts: true,
                };

                const map = new window.kakao.maps.Map(container, options);
                mapInstanceRef.current = map;

                // 지도 타입 컨트롤 추가
                const mapTypeControl = new window.kakao.maps.MapTypeControl();
                map.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT);

                // 줌 컨트롤 추가
                const zoomControl = new window.kakao.maps.ZoomControl();
                map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

                setMapLoaded(true);

                // 마커 생성
                setTimeout(() => {
                    createMarkersWithOverlay();
                }, 100);
            } catch (error) {
                console.error("카카오맵 초기화 실패:", error);
            }
        };

        // 이미 로드된 경우 처리
        if (window.kakao && window.kakao.maps) {
            const checkAndInitMap = () => {
                if (window.kakao.maps.LatLng) {
                    initializeMap();
                } else if (window.kakao.maps.load) {
                    window.kakao.maps.load(() => {
                        initializeMap();
                    });
                } else {
                    setTimeout(checkAndInitMap, 100);
                }
            };
            checkAndInitMap();
            return;
        }

        // 스크립트 동적 로드
        const script = document.createElement("script");
        script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${import.meta.env.VITE_KAKAO_MAP_KEY}&autoload=false`;
        script.async = true;
        document.head.appendChild(script);

        script.onload = () => {
            window.kakao.maps.load(() => {
                initializeMap();
            });
        };

        return () => {
            // 클린업
            markersRef.current.forEach((marker) => marker.setMap(null));
            overlaysRef.current.forEach((overlay) => overlay.setMap(null));
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
            }
        };
    }, []);

    // 악취 확산 오버레이 생성
    const createSpreadOverlay = (location: MarkerData) => {
        const angle = getWindAngle(location.data.windDirection);
        const offset = getWindOffset(location.data.windDirection);
        const imageSrc = `/img/${location.type}_${angle}.png`;

        // 이미지 크기 (이미지 실제 크기에 맞춰 조정 필요)
        const imageWidth = 220;
        const imageHeight = 220;

        return `
            <div style="position: absolute; 
                        width: ${imageWidth}px; 
                        height: ${imageHeight}px; 
                        left: ${-(imageWidth/2) + offset.x}px; 
                        top: ${-(imageHeight/2) + offset.y}px; 
                        pointer-events: none;">
                <img src="${imageSrc}" 
                     alt="악취 확산" 
                     style="width: 220px; height: 220px; object-fit: contain;" />
            </div>
        `;
    };

    // 마커와 오버레이 생성
    const createMarkersWithOverlay = () => {
        if (!mapInstanceRef.current) return;

        // 기존 마커 제거
        markersRef.current.forEach((marker) => marker.setMap(null));
        overlaysRef.current.forEach((overlay) => overlay.setMap(null));
        markersRef.current = [];
        overlaysRef.current = [];

        mockData.forEach((location) => {
            const position = new window.kakao.maps.LatLng(location.position.lat, location.position.lng);

            // 악취 확산 오버레이 (마커보다 먼저 생성하여 아래 레이어에 위치)
            const spreadOverlay = new window.kakao.maps.CustomOverlay({
                position: position,
                content: createSpreadOverlay(location),
                yAnchor: 0.5,
                xAnchor: 0.5,
                zIndex: 1
            });
            spreadOverlay.setMap(mapInstanceRef.current);
            overlaysRef.current.push(spreadOverlay);

            // 마커 이미지 설정
            const imageSrc = getMarkerImage(location.type);
            const imageSize = new window.kakao.maps.Size(50, 50);
            const imageOption = { offset: new window.kakao.maps.Point(25, 25) };
            const markerImage = new window.kakao.maps.MarkerImage(imageSrc, imageSize, imageOption);

            const marker = new window.kakao.maps.Marker({
                position: position,
                map: mapInstanceRef.current,
                image: markerImage,
                zIndex: 2
            });

            // 마커 클릭 이벤트
            window.kakao.maps.event.addListener(marker, "click", () => {
                showInfoWindow(location, marker);
            });

            markersRef.current.push(marker);
        });
    };

    const getMarkerImage = (type: string) => {
        const images = {
            good: "/img/ds_ico01.png",
            normal: "/img/ds_ico02.png",
            warning: "/img/ds_ico03.png",
            danger: "/img/ds_ico04.png",
        };

        return images[type as keyof typeof images] || "/img/ds_ico02.png";
    };

    const showInfoWindow = (location: MarkerData, marker: any) => {
        setSelectedMarker(location);

        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        const currentSensor = "종합";
        const content = createInfoWindowContent(location, currentSensor);

        const infoWindow = new window.kakao.maps.InfoWindow({
            content: content,
            removable: true,
            zIndex: 3,
        });

        infoWindow.open(mapInstanceRef.current, marker);
        infoWindowRef.current = infoWindow;

        setTimeout(() => {
            attachInfoWindowEvents(location, currentSensor);
        }, 100);
    };

    const createInfoWindowContent = (location: MarkerData, currentSensor: string = "종합") => {
        const currentData =
            currentSensor === "종합" ? location.data : location.sensors[currentSensor] || location.data;

        const getStatus = (value: number, type: string) => {
            if (type === "nh3") {
                return value < 10 ? "좋음" : value < 25 ? "보통" : value < 40 ? "나쁨" : "매우나쁨";
            }
            return value < 2 ? "좋음" : value < 5 ? "보통" : value < 8 ? "나쁨" : "매우나쁨";
        };

        const getStatusClass = (status: string) => {
            switch (status) {
                case "좋음":
                    return "good";
                case "보통":
                    return "normal";
                case "나쁨":
                    return "bad";
                case "매우나쁨":
                    return "very-bad";
                default:
                    return "";
            }
        };

        return `
      <div class="info-window" style="
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        width: 475px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div class="info-header" style="
          background-color: #24aca7;
          padding: 12px 15px;
          color: white;
        ">
          <div class="weather-section" style="display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 18px; font-weight: 700; color:#ffffff; ">${location.name} (기기수량${location.sensorCount})</h3>
            <span style="font-size: 18px; font-weight: 700; margin-right:20px; color:#ffffff;">${currentTime.toLocaleString(
            "ko-KR",
            {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }
        )}</span>
          </div>
        </div>
        
        <div class="sensor-tabs" style="
        margin-bottom: 0px;
          display: flex;
          background-color: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
        ">
          <button class="sensor-tab ${currentSensor === "종합" ? "active" : ""}" data-sensor="종합" style="
            flex: 1;
            padding: 10px 5px;
            background: ${currentSensor === "종합" ? "#4a9b94" : "none"};
            border: none;
            cursor: pointer;
              font-size: ${currentSensor === "종합" ? "16px" : "14px"};
            color: ${currentSensor === "종합" ? "#ffffff" : "#666"};
            font-weight: 500;
          ">종합</button>
          ${Object.keys(location.sensors)
            .map(
                (sensor) =>
                    `<button class="sensor-tab ${currentSensor === sensor ? "active" : ""}" data-sensor="${sensor}" style="
              flex: 1;
              padding: 10px 5px;
              background: ${currentSensor === sensor ? "white" : "none"};
              border: none;
              cursor: pointer;
              font-size: ${currentSensor === sensor ? "16px" : "14px"};
              color: ${currentSensor === sensor ? "#5AC8FA" : "#666"};
              border-radius: 4px 4px 0 0;
              font-weight: 500;
            ">${sensor}</button>`
            )
            .join("")}
        </div>
        
        <div class="data-grid" style="padding: 15px; display: flex; gap: 15px;">
          <div style="flex: 1;">
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">복합악취</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(배수) :</span>
              <span class="data-value" style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${currentData.nh3.toFixed(2)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${getStatusClass(getStatus(currentData.nh3, "nh3")) === "good"
            ? "#4CAF50"
            : getStatusClass(getStatus(currentData.nh3, "nh3")) === "normal"
                ? "#2196F3"
                : getStatusClass(getStatus(currentData.nh3, "nh3")) === "bad"
                    ? "#FF9800"
                    : "#F44336"
        };
                background-color: ${getStatusClass(getStatus(currentData.nh3, "nh3")) === "good"
            ? "#E8F5E9"
            : getStatusClass(getStatus(currentData.nh3, "nh3")) === "normal"
                ? "#E3F2FD"
                : getStatusClass(getStatus(currentData.nh3, "nh3")) === "bad"
                    ? "#FFF3E0"
                    : "#FFEBEE"
        };
              ">${getStatus(currentData.nh3, "nh3")}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">황화수소</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span class="data-value" style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${currentData.h2s.toFixed(2)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${getStatusClass(getStatus(currentData.h2s, "h2s")) === "good"
            ? "#4CAF50"
            : getStatusClass(getStatus(currentData.h2s, "h2s")) === "normal"
                ? "#2196F3"
                : getStatusClass(getStatus(currentData.h2s, "h2s")) === "bad"
                    ? "#FF9800"
                    : "#F44336"
        };
                background-color: ${getStatusClass(getStatus(currentData.h2s, "h2s")) === "good"
            ? "#E8F5E9"
            : getStatusClass(getStatus(currentData.h2s, "h2s")) === "normal"
                ? "#E3F2FD"
                : getStatusClass(getStatus(currentData.h2s, "h2s")) === "bad"
                    ? "#FFF3E0"
                    : "#FFEBEE"
        };
              ">${getStatus(currentData.h2s, "h2s")}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">암모니아</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span class="data-value" style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${currentData.ch3sh.toFixed(2)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "good"
            ? "#4CAF50"
            : getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "normal"
                ? "#2196F3"
                : getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "bad"
                    ? "#FF9800"
                    : "#F44336"
        };
                background-color: ${getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "good"
            ? "#E8F5E9"
            : getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "normal"
                ? "#E3F2FD"
                : getStatusClass(getStatus(currentData.ch3sh, "ch3sh")) === "bad"
                    ? "#FFF3E0"
                    : "#FFEBEE"
        };
              ">${getStatus(currentData.ch3sh, "ch3sh")}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0; ${currentSensor === "종합" ? "" : ""}">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">메탄가스</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span class="data-value" style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${currentData.tvoc.toFixed(2)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "good"
            ? "#4CAF50"
            : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "normal"
                ? "#2196F3"
                : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "bad"
                    ? "#FF9800"
                    : "#F44336"
        };
                background-color: ${getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "good"
            ? "#E8F5E9"
            : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "normal"
                ? "#E3F2FD"
                : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "bad"
                    ? "#FFF3E0"
                    : "#FFEBEE"
        };
              ">${getStatus(currentData.tvoc, "tvoc")}</span>
            </div>
            
            ${currentSensor === "종합"
            ? `
              <div style="border-top: 2px solid #e0e0e0; margin-top: 15px; padding-top: 15px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 5px;">
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">온도</span>
                    <span style="font-size: 14px; font-weight: 600; color: #FF9800;">${location.data.temperature}℃</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">습도</span>
                    <span style="font-size: 14px; font-weight: 600; color: #2196F3;">${location.data.humidity}%</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">풍향</span>
                    <span style="font-size: 14px; font-weight: 600; color: #333;">${location.data.windDirection}</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">풍속</span>
                    <span style="font-size: 14px; font-weight: 600; color: #4CAF50;">${location.data.windSpeed}m/s</span>
                  </div>
                </div>
              </div>
            `
            : ""
        }
          </div>
          
          <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 0px; gap: 20px;">
            <div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
              <img src="${location.type === "good"
            ? "/img/ds_ico01.png"
            : location.type === "normal"
                ? "/img/ds_ico02.png"
                : location.type === "warning"
                    ? "/img/ds_ico03.png"
                    : "/img/ds_ico04.png"
        }" alt="${location.type === "good"
            ? "좋음"
            : location.type === "normal"
                ? "보통"
                : location.type === "warning"
                    ? "나쁨"
                    : "매우나쁨"
        }" style="width: 60px; height: 60px;" />
              <span style="font-size: 18px; color: #333; font-weight: 700;">${location.type === "good"
            ? "좋음"
            : location.type === "normal"
                ? "보통"
                : location.type === "warning"
                    ? "나쁨"
                    : "매우나쁨"
        }</span>
            </div>
            <button class="graph-button" style="
              background-color: #5AC8FA;
              color: white;
              border: none;
              padding: 3px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 15px;
              font-weight: 600;
              line-height: 1.3;
            ">그래프<br>보기</button>
          </div>
        </div>
      </div>
    `;
    };

    const attachInfoWindowEvents = (location: MarkerData, currentSensor: string) => {
        let activeSensor = currentSensor;

        const sensorTabs = document.querySelectorAll(".sensor-tab");
        sensorTabs.forEach((tab) => {
            tab.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();

                const sensor = (e.target as HTMLElement).getAttribute("data-sensor");
                if (sensor && sensor !== activeSensor) {
                    activeSensor = sensor;

                    sensorTabs.forEach((t) => {
                        (t as HTMLElement).style.background = "none";
                        (t as HTMLElement).style.color = "#666";
                    });

                    (e.target as HTMLElement).style.background = "#4A9B94";
                    (e.target as HTMLElement).style.color = "white";

                    const currentData =
                        sensor === "종합" ? location.data : location.sensors[sensor] || location.data;

                    const dataValues = document.querySelectorAll(".data-value");
                    if (dataValues[0]) dataValues[0].textContent = currentData.nh3.toFixed(2);
                    if (dataValues[1]) dataValues[1].textContent = currentData.h2s.toFixed(2);
                    if (dataValues[2]) dataValues[2].textContent = currentData.ch3sh.toFixed(2);
                    if (dataValues[3]) dataValues[3].textContent = currentData.tvoc.toFixed(2);
                }
            });
        });

        const graphBtn = document.querySelector(".graph-button");
        if (graphBtn) {
            graphBtn.addEventListener("click", () => {
                alert("그래프 보기 기능 - 구현 예정");
            });
        }
    };

    // 날씨 위젯 컴포넌트
    const WeatherWidget = () => {
        // 풍향을 각도로 변환 (북풍이 0도 기준)
        const getWindDirectionAngle = (direction: string): number => {
            const angles: { [key: string]: number } = {
                '북': 0,
                '북동': 45,
                '동': 90,
                '남동': 135,
                '남': 180,
                '남서': 225,
                '서': 270,
                '북서': 315
            };
            return angles[direction] || 0;
        };

        return (
            <div style={{
                position: 'absolute',
                top: '60px',
                right: '60px',
                backgroundColor: '#24ADA7',
                borderRadius: '16px',
                padding: '0',
                width: '320px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                overflow: 'hidden'
            }}>
                {/* 헤더 섹션 */}
                <div style={{
                    backgroundColor: '#24ADA7',
                    color: 'white',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>빛가람동</span>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff', marginLeft: '10px'}}>복동풍</span>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff', marginLeft: '10px'}}>
                        {currentTime.toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        }).replace(/\. /g, '.').replace(/\./g, '.').substring(0, 16)}
                    </span>
                </div>

                {/* 날씨 정보 섹션 */}
                <div style={{
                    backgroundColor: 'white',
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    {/* 풍향 아이콘 */}
                    <div style={{
                        width: '76px',
                        height: '76px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <img
                            src="/img/wind_dir01.png"
                            alt="풍향"
                            style={{
                                width: '76px',
                                height: '76px',
                                transform: `rotate(${getWindDirectionAngle('북동')}deg)`,
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    </div>

                    {/* 날씨 데이터 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1, flexDirection: 'row'}}>
                        <div style={{marginBottom: '0px' }}>
                            <span style={{ textAlign: 'center', display: 'block',fontSize: '14px', color: '#666' }}>온도</span>
                            <span style={{ marginTop: '5px', textAlign: 'center',display: 'block', fontSize: '20px', fontWeight: 'bold', color: '#0b7a76' }}>17.4°C</span>
                        </div>
                        <div style={{ marginBottom: '0px' }}>
                            <span style={{ textAlign: 'center',display: 'block',fontSize: '14px', color: '#666' }}>습도</span>
                            <span style={{ marginTop: '5px', textAlign: 'center',display: 'block',fontSize: '20px', fontWeight: 'bold', color: '#0b7a76' }}>21%</span>
                        </div>
                        <div>
                            <span style={{ textAlign: 'center',display: 'block',fontSize: '14px', color: '#666' }}>기압</span>
                            <span style={{ marginTop: '5px', textAlign: 'center',display: 'block',fontSize: '20px', fontWeight: 'bold', color: '#0b7a76' }}>1022.3</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                width: "100%",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f0f0f0",
                overflow: "hidden",
            }}
        >
            {/* 상단 헤더 */}
            <div
                style={{
                    backgroundColor: "#2C2C2C",
                    padding: "10px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
          <span
              style={{
                  color: "white",
                  padding: "6px 5px 6px 10px",
                  fontSize: "13px",
              }}
          >
            주소검색
          </span>

                    <select
                        style={{
                            backgroundColor: "#444",
                            color: "white",
                            border: "1px solid #555",
                            padding: "6px 10px",
                            borderRadius: "3px",
                            fontSize: "13px",
                            cursor: "pointer",
                        }}
                        disabled
                    >
                        <option>전라남도</option>
                    </select>

                    <select
                        style={{
                            backgroundColor: "#444",
                            color: "white",
                            border: "1px solid #555",
                            padding: "6px 10px",
                            borderRadius: "3px",
                            fontSize: "13px",
                            cursor: "pointer",
                        }}
                        disabled
                    >
                        <option>나주시</option>
                    </select>

                    <select
                        style={{
                            backgroundColor: "#444",
                            color: "white",
                            border: "1px solid #555",
                            padding: "6px 10px",
                            borderRadius: "3px",
                            fontSize: "13px",
                            cursor: "pointer",
                        }}
                    >
                        <option>빛가람동</option>
                        <option>금천면</option>
                        <option>봉황면</option>
                        <option>다시면</option>
                        <option>문평면</option>
                        <option>노안면</option>
                    </select>

                    <span
                        style={{
                            color: "white",
                            padding: "6px 5px 6px 10px",
                            fontSize: "13px",
                        }}
                    >
            검색어
          </span>
                    <input
                        type="text"
                        placeholder="사업장명을 입력해 주세요."
                        style={{
                            backgroundColor: "#444",
                            color: "white",
                            border: "1px solid #555",
                            padding: "6px 12px",
                            borderRadius: "3px",
                            width: "180px",
                            fontSize: "13px",
                        }}
                    />

                    <button
                        style={{
                            backgroundColor: "transparent",
                            backgroundImage: "url(/img/ico_sch02.png)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "16px",
                            width: "30px",
                            height: "30px",
                        }}
                    >
                    </button>
                </div>

                <button
                    style={{
                        backgroundColor: "#5AC8FA",
                        color: "white",
                        border: "none",
                        padding: "8px 20px",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "13px",
                    }}
                >
                    측정소별 숨기기
                </button>
            </div>

            {/* 메인 컨텐츠 */}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* 좌측 범례 */}
                <div
                    style={{
                        position: "absolute",
                        left: "20px",
                        top: "20px",
                        display: "flex",
                        gap: "6px",
                        backgroundColor: "rgba(0,0,0,0.63)",
                        borderRadius: "8px",
                        padding: "15px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 100,
                    }}
                >
                    <div
                        style={{
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "10px",
                        }}
                    >
                        <img src="/img/ds_ico01.png" alt="좋음" style={{ width: "35px", height: "35px" }} />
                        <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>좋음</span>
                    </div>
                    <div
                        style={{
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "10px",
                        }}
                    >
                        <img src="/img/ds_ico02.png" alt="보통" style={{ width: "35px", height: "35px" }} />
                        <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>보통</span>
                    </div>
                    <div
                        style={{
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "10px",
                        }}
                    >
                        <img src="/img/ds_ico03.png" alt="나쁨" style={{ width: "35px", height: "35px" }} />
                        <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>나쁨</span>
                    </div>
                    <div
                        style={{
                            alignItems: "center",
                            gap: "10px",
                        }}
                    >
                        <img src="/img/ds_ico04.png" alt="매우나쁨" style={{ width: "35px", height: "35px" }} />
                        <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>
              매우
              <br />
              나쁨
            </span>
                    </div>
                </div>

                {/* 날씨 위젯 */}
                {mapLoaded && <WeatherWidget />}

                {/* 지도 영역 */}
                <div style={{ flex: 1, position: "relative" }}>
                    <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                </div>
            </div>
        </div>
    );
};

export default AIView;